import { useEffect, useMemo, useState } from 'react';
import { Steps, Spin, Alert, Select, Input, message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import * as claus from 'src/_shared/services/claus/endpoints';
import { buildCreatePayload, recordToFieldMappingRows, validateSchedule } from 'src/_shared/services/claus/serde';
import { TEMPLATE_TYPE_ORDER } from 'src/_shared/services/claus/constants';
import ConsoleHeader from '../console/ConsoleHeader.jsx';
import Section from '../console/Section.jsx';
import { useTemplates } from './hooks/useClaus';
import CredentialsSection from './sections/CredentialsSection.jsx';
import InputParamsSection from './sections/InputParamsSection.jsx';
import ScheduleSection from './sections/ScheduleSection.jsx';
import StatusMappingsSection from './sections/StatusMappingsSection.jsx';
import Field from './sections/Field.jsx';

const emptyForm = {
	name: '',
	templateId: '',
	authParams: {},
	inputParams: {},
	fieldMappingRows: [],
	statusEntries: [],
	schedule: { frequency: 1, unit: 'hour' },
	sftpCredentialId: undefined,
};

// Per-step editorial headers (H2 + description above the card), in the guided style.
const STEP_COPY = {
	template: {
		title: 'Choose a template',
		description: 'Templates define the source system and the record type it syncs.',
	},
	connect: {
		title: 'Connect to the source',
		description: 'Name the integration and add its credentials and parameters.',
	},
	status: {
		title: 'Status mappings',
		description: 'Map incoming candidate statuses to ERIN statuses and stages.',
	},
	finish: {
		title: 'Schedule & create',
		description: 'Set the sync cadence, test the connection, and create the integration.',
	},
};

export default function CreateIntegrationWizard({ orgId, company, onCancel, onCreated }) {
	const { loading, data, error } = useTemplates();
	const [current, setCurrent] = useState(0);
	const [form, setForm] = useState(emptyForm);
	const [template, setTemplate] = useState(null);
	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState(null);
	const [saving, setSaving] = useState(false);

	const templates = useMemo(() => {
		const items = data?.items || [];
		// group by type (jobs first), then alphabetically within each type
		return [...items].sort(
			(a, b) =>
				(TEMPLATE_TYPE_ORDER[a.type] ?? 9) - (TEMPLATE_TYPE_ORDER[b.type] ?? 9) ||
				a.name.localeCompare(b.name),
		);
	}, [data]);

	const patch = (partial) => setForm((f) => ({ ...f, ...partial }));
	const isCandidate = template?.type === 'candidate';
	const isSftp = template?.source === 'sftp';
	const isScheduled = template?.source === 'ats' || template?.source === 'metabase';

	function selectTemplate(t) {
		setTemplate(t);
		setForm({
			...emptyForm,
			templateId: t.id,
			name: '',
			fieldMappingRows: recordToFieldMappingRows(t.defaultFieldMappings || {}),
		});
		setTestResult(null);
	}

	async function testConnection() {
		setTesting(true);
		setTestResult(null);
		try {
			const res = await claus.testConnection(orgId, {
				templateId: form.templateId,
				inputParams: form.inputParams,
				authParams: form.authParams,
			});
			setTestResult(res?.success === false ? { ok: false, msg: (res.errors || []).join(', ') || 'Failed' } : { ok: true });
		} catch (e) {
			setTestResult({ ok: false, msg: e.message });
		} finally {
			setTesting(false);
		}
	}

	async function create() {
		if (!form.name.trim()) {
			message.error('Give the integration a name.');
			return;
		}
		if (isScheduled) {
			const err = validateSchedule(form.schedule);
			if (err) {
				message.error(err);
				return;
			}
		}
		setSaving(true);
		try {
			// Provision the CLAUS org (id == companyId) if it doesn't exist yet.
			await claus.ensureOrg(orgId, company?.name || 'Organization');
			const payload = buildCreatePayload(form, template);
			const res = await claus.createIntegration(orgId, payload);
			message.success('Integration created');
			onCreated(res.id);
		} catch (e) {
			message.error(e.message || 'Could not create the integration.');
		} finally {
			setSaving(false);
		}
	}

	if (loading) {
		return (
			<div className="js-loading">
				<Spin />
			</div>
		);
	}
	if (error) {
		return <Alert type="error" showIcon message="Could not load templates" description={error.message} />;
	}

	// Field mappings are defined by the selected template (claraty normalization), so the wizard no
	// longer exposes a source field-mapping step. Candidates still need status mappings.
	const stepDefs = [
		{ key: 'template', title: 'Template' },
		{ key: 'connect', title: 'Connect' },
		...(isCandidate ? [{ key: 'status', title: 'Status mappings' }] : []),
		{ key: 'finish', title: 'Schedule & create' },
	];
	const stepKey = stepDefs[current]?.key;
	const copy = STEP_COPY[stepKey] || {};

	return (
		<>
			<ConsoleHeader
				onBack={onCancel}
				title="Create integration"
				description="Pick a template, connect the source, and schedule the first sync."
			/>

			<Steps className="ei-wizard-steps" current={current} items={stepDefs.map((s) => ({ title: s.title }))} />

			<Section title={copy.title} description={copy.description} bare={stepKey === 'template'}>
				{stepKey === 'template' && (
					<div className="ei-template-grid">
						{templates.map((t) => (
							<button
								key={t.id}
								type="button"
								className={`ei-template-card${template?.id === t.id ? ' is-selected' : ''}`}
								onClick={() => selectTemplate(t)}
							>
								<div className="ei-template-name">{t.name}</div>
								<div className="ei-template-type">
									{t.type} · {t.source}
								</div>
								{t.description && <div className="js-muted" style={{ marginTop: 6 }}>{t.description}</div>}
							</button>
						))}
					</div>
				)}

				{stepKey === 'connect' && template && (
					<>
						<Field label="Integration name" required>
							<div className="js-token">
								<Input
									value={form.name}
									placeholder="e.g. Production Greenhouse"
									onChange={(e) => patch({ name: e.target.value })}
								/>
							</div>
						</Field>
						{isSftp ? (
							<SftpCredentialPicker orgId={orgId} value={form.sftpCredentialId} onChange={(v) => patch({ sftpCredentialId: v })} />
						) : (
							<>
								<div className="ei-minilabel has-rule">Credentials</div>
								<CredentialsSection
									template={template}
									authParams={form.authParams}
									onChange={(a) => {
										patch({ authParams: a });
										setTestResult(null);
									}}
								/>
							</>
						)}
						<div className="ei-minilabel has-rule">Input parameters</div>
						<InputParamsSection
							key={template.id}
							template={template}
							inputParams={form.inputParams}
							onChange={(p) => {
								patch({ inputParams: p });
								setTestResult(null);
							}}
						/>
					</>
				)}

				{stepKey === 'status' && template && (
					<StatusMappingsSection entries={form.statusEntries} onChange={(e) => patch({ statusEntries: e })} />
				)}

				{stepKey === 'finish' && template && (
					<>
						{isScheduled && (
							<ScheduleSection
								schedule={form.schedule}
								onChange={(s) => patch({ schedule: s })}
								showLookback={!!template.lookbackSupported && (template.type === 'job' || template.type === 'candidate')}
							/>
						)}
						{!isSftp && (
							<div style={{ marginTop: isScheduled ? 20 : 0 }}>
								<button type="button" className="js-btn" onClick={testConnection} disabled={testing}>
									<ThunderboltOutlined /> {testing ? 'Testing…' : 'Test connection'}
								</button>
								{testResult && (
									<Alert
										style={{ marginTop: 10 }}
										type={testResult.ok ? 'success' : 'error'}
										showIcon
										message={testResult.ok ? 'Connection successful' : `Connection failed: ${testResult.msg}`}
									/>
								)}
							</div>
						)}
					</>
				)}
			</Section>

			<div className="ei-wizard-foot">
				<button type="button" className="js-btn" onClick={current === 0 ? onCancel : () => setCurrent((c) => c - 1)}>
					{current === 0 ? 'Cancel' : 'Back'}
				</button>
				{current < stepDefs.length - 1 ? (
					<button
						type="button"
						className="js-btn js-btn-primary"
						disabled={current === 0 && !template}
						onClick={() => setCurrent((c) => c + 1)}
					>
						Next
					</button>
				) : (
					<button type="button" className="js-btn js-btn-primary" onClick={create} disabled={saving}>
						{saving ? 'Creating…' : 'Create integration'}
					</button>
				)}
			</div>
		</>
	);
}

function SftpCredentialPicker({ orgId, value, onChange }) {
	const [creds, setCreds] = useState([]);
	useEffect(() => {
		let alive = true;
		claus
			.listSftpCredentials(orgId)
			.then((r) => alive && setCreds(Array.isArray(r) ? r : r?.items || []))
			.catch(() => alive && setCreds([]));
		return () => {
			alive = false;
		};
	}, [orgId]);
	return (
		<Field label="SFTP credential" hint="Leave blank to auto-create a new credential on save.">
			<Select
				allowClear
				style={{ width: '100%' }}
				value={value}
				onChange={onChange}
				placeholder="Create new credential"
				options={creds.map((c) => ({ value: c.id, label: `${c.name} (${c.username})` }))}
			/>
		</Field>
	);
}
